/**
 * Admin API Client
 *
 * Thin wrapper around fetch() that:
 *  1. attaches the current Firebase ID token as a Bearer,
 *  2. targets /api/admin/* endpoints,
 *  3. surfaces JSON errors as thrown Error objects.
 *
 * Server side: requireAdmin middleware (Firebase verify) + service_role Supabase.
 *
 * Example:
 *   const shops = await adminApi.shops.list();
 *   const shop = await adminApi.shops.get(id);
 *   await adminApi.shops.update(id, { source_filters: {...} });
 */

import { auth } from '../config/firebase';

async function getAuthHeader(): Promise<Record<string, string>> {
  // Wait for Firebase to restore the session from IndexedDB.
  // On page load auth.currentUser is briefly null until onAuthStateChanged fires.
  if (typeof auth.authStateReady === 'function') {
    await auth.authStateReady();
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not logged in (no Firebase user)');
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

interface RequestOptions {
  /** Return the Response object instead of parsed body. Used when caller needs headers. */
  rawResponse?: boolean;
}

async function request<T = any>(
  method: string,
  path: string,
  body?: unknown,
  opts?: RequestOptions
): Promise<T> {
  const authHeader = await getAuthHeader();
  const res = await fetch(path, {
    method,
    headers: {
      ...authHeader,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j.message || j.error || msg;
    } catch {
      // ignore
    }
    throw new Error(`${method} ${path} failed: ${msg}`);
  }

  if (opts?.rawResponse) return res as unknown as T;

  // Some endpoints (DELETE) may return empty body
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) sp.set(k, v.join(','));
    } else if (typeof v === 'boolean') {
      sp.set(k, v ? '1' : '0');
    } else {
      sp.set(k, String(v));
    }
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}

export const adminApi = {
  shops: {
    list: () => request<any[]>('GET', '/api/admin/shops'),
    get: (id: string) => request<any>('GET', `/api/admin/shops/${id}`),
    create: (body: any) => request<any>('POST', '/api/admin/shops', body),
    update: (id: string, body: any) =>
      request<any>('PUT', `/api/admin/shops/${id}`, body),
    remove: (id: string) =>
      request<{ success: true; id: string }>(
        'DELETE',
        `/api/admin/shops/${id}`
      ),
  },
  syncLogs: {
    list: (limit = 10, shopId?: string) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (shopId) params.set('shopId', shopId);
      return request<any[]>('GET', `/api/admin/sync-logs?${params.toString()}`);
    },
  },
  feedImportLogs: {
    list: (limit = 10) =>
      request<any[]>('GET', `/api/admin/feed-import-logs?limit=${limit}`),
  },
  suppliers: {
    list: () => request<any[]>('GET', '/api/admin/suppliers'),
    get: (id: string | number) =>
      request<any>('GET', `/api/admin/suppliers/${id}`),
    create: (body: any) => request<any>('POST', '/api/admin/suppliers', body),
    update: (id: string | number, body: any) =>
      request<any>('PUT', `/api/admin/suppliers/${id}`, body),
    remove: (id: string | number) =>
      request<{ success: true; id: string }>(
        'DELETE',
        `/api/admin/suppliers/${id}`
      ),
  },
  products: {
    /**
     * List products with optional filters. Returns either the array of rows,
     * or `{ rows, total }` when `withCount` is true.
     */
    list: async (
      params: {
        sku?: string;
        skus?: string[];
        dobavljac?: string[];
        status?: string[];
        stock_status?: string[];
        min_stock_quantity?: number;
        has_categories?: boolean;
        has_specifikacije?: boolean;
        has_dobavljac?: boolean;
        search?: string;
        fields?: string[];
        order_by?: string;
        order_dir?: 'asc' | 'desc';
        offset?: number;
        limit?: number;
        random?: boolean;
      } = {},
      withCount = false
    ): Promise<any[] | { rows: any[]; total: number }> => {
      const qs = buildQuery({
        ...params,
        count: withCount ? 'exact' : undefined,
      });
      if (!withCount) {
        return request<any[]>('GET', `/api/admin/products${qs}`);
      }
      const res = await request<Response>(
        'GET',
        `/api/admin/products${qs}`,
        undefined,
        { rawResponse: true }
      );
      const total = parseInt(res.headers.get('X-Total-Count') || '0', 10) || 0;
      const rows = (await res.json()) as any[];
      return { rows, total };
    },

    getBySku: (sku: string) =>
      request<any>('GET', `/api/admin/products/by-sku/${encodeURIComponent(sku)}`),

    insert: (rows: any[]) =>
      request<any[]>('POST', '/api/admin/products/insert', rows),

    upsert: (rows: any[], onConflict = 'sku') =>
      request<any[]>(
        'POST',
        `/api/admin/products/upsert?on_conflict=${encodeURIComponent(onConflict)}`,
        rows
      ),

    stats: () =>
      request<{
        totalProducts: number;
        availableProducts: number;
        supplierCounts: Record<string, number>;
        duplicateSkus: Array<{ sku: string; count: number }>;
      }>('GET', '/api/admin/products/stats'),

    smartBulkUpsert: (payload: {
      supplierName: string;
      products: any[];
      updateFields?: Record<string, boolean>;
    }) =>
      request<{
        data: any[];
        stats: { created: number; updated: number; total: number };
      }>('POST', '/api/admin/products/smart-bulk-upsert', payload),
  },
  supplierPriority: {
    list: () => request<any[]>('GET', '/api/admin/supplier-priority'),
    upsert: (
      rowOrRows:
        | { supplier_name: string; priority: number }
        | Array<{ supplier_name: string; priority: number }>
    ) => request<any[]>('POST', '/api/admin/supplier-priority', rowOrRows),
    remove: (supplierName: string) =>
      request<{ success: true; supplier_name: string }>(
        'DELETE',
        `/api/admin/supplier-priority/${encodeURIComponent(supplierName)}`
      ),
  },
  customFields: {
    list: () => request<any[]>('GET', '/api/admin/custom-fields'),
    create: (body: any) => request<any>('POST', '/api/admin/custom-fields', body),
    update: (id: number, body: any) =>
      request<any>('PUT', `/api/admin/custom-fields/${id}`, body),
    remove: (id: number) =>
      request<{ success: true; id: number }>(
        'DELETE',
        `/api/admin/custom-fields/${id}`
      ),
  },
  productChanges: {
    list: (opts: { pending?: boolean; limit?: number } = {}) =>
      request<any[]>(
        'GET',
        `/api/admin/product-changes${buildQuery({
          pending: opts.pending,
          limit: opts.limit,
        })}`
      ),
    markSynced: (ids: number[]) =>
      request<{ success: true; count: number }>(
        'POST',
        '/api/admin/product-changes/mark-synced',
        { ids }
      ),
  },
  specValueMappings: {
    list: (targetAttribute?: string) =>
      request<any[]>(
        'GET',
        `/api/admin/spec-value-mappings${buildQuery({ target_attribute: targetAttribute })}`
      ),
    create: (body: {
      target_attribute: string;
      original_value: string;
      normalized_value: string;
    }) => request<any>('POST', '/api/admin/spec-value-mappings', body),
    remove: (id: number) =>
      request<{ success: true; id: number }>(
        'DELETE',
        `/api/admin/spec-value-mappings/${id}`
      ),
    unmapped: (targetAttribute: string) =>
      request<any[]>(
        'GET',
        `/api/admin/spec-value-mappings/unmapped${buildQuery({ target_attribute: targetAttribute })}`
      ),
  },
  mainAttributes: {
    list: () =>
      request<{ attributes: Array<{ slug: string; label: string }> }>(
        'GET',
        '/api/admin/attributes/main'
      ),
  },
  productSyncStatus: {
    stats: (shopId: string) =>
      request<{
        pending: number;
        processing: number;
        done: number;
        total: number;
      }>(
        'GET',
        `/api/admin/product-sync-status/stats${buildQuery({ shopId })}`
      ),
  },
  syncHealth: {
    get: () => request<any>('GET', '/api/admin/sync-health'),
  },
  cron: {
    /** Same { data, error } shape as supabase.rpc(), so callers did not have to change. */
    rpc: async (fn: string, args: Record<string, unknown> = {}) => {
      try {
        const res = await request<{ data: any }>('POST', '/api/admin/cron-rpc', { fn, args });
        return { data: res?.data ?? null, error: null as null | { message: string } };
      } catch (e: any) {
        return { data: null, error: { message: e?.message || String(e) } };
      }
    },
  },
};
